import * as React from 'react';

import { Menu } from '@base-ui/react/menu';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from 'utils/cn';

const ShadDropdownMenu = Menu.Root;

type TriggerProps = React.ComponentPropsWithoutRef<typeof Menu.Trigger> & {
  asChild?: boolean;
};

const ShadDropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  TriggerProps
>(({ asChild = false, children, className, ...props }, ref) => {
  const render =
    asChild && React.isValidElement(children)
      ? (children as React.ReactElement)
      : undefined;

  return (
    <Menu.Trigger ref={ref} className={className} render={render} {...props}>
      {render ? undefined : children}
    </Menu.Trigger>
  );
});
ShadDropdownMenuTrigger.displayName = 'ShadDropdownMenuTrigger';

type ContentProps = React.ComponentPropsWithoutRef<typeof Menu.Popup> & {
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  keepMounted?: boolean;
};

const ShadDropdownMenuContent = React.forwardRef<HTMLDivElement, ContentProps>(
  (
    {
      className,
      align = 'start',
      side = 'bottom',
      sideOffset = 4,
      keepMounted = false,
      ...props
    },
    ref,
  ) => (
    <Menu.Portal keepMounted={keepMounted}>
      <Menu.Positioner align={align} side={side} sideOffset={sideOffset}>
        <Menu.Popup
          ref={ref}
          className={cn(
            'z-dropdown max-h-[var(--available-height,80vh)] min-w-[12rem] overflow-x-hidden overflow-y-auto rounded-medium border border-border-subtle bg-background p-xxs1 shadow-overlay outline-none transition-[opacity,transform] data-[closed]:pointer-events-none data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            className,
          )}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  ),
);
ShadDropdownMenuContent.displayName = 'ShadDropdownMenuContent';

type ItemProps = React.ComponentPropsWithoutRef<typeof Menu.Item> & {
  inset?: boolean;
};

const ShadDropdownMenuItem = React.forwardRef<HTMLElement, ItemProps>(
  ({ className, inset = false, ...props }, ref) => (
    <Menu.Item
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-xs2 rounded-xSmall px-xs3 py-xs2 text-body-small text-primary outline-none transition-colors [&>svg]:shrink-0 [&>svg]:size-sm1 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-background-hover',
        inset && 'pl-sm3',
        className,
      )}
      {...props}
    />
  ),
);
ShadDropdownMenuItem.displayName = 'ShadDropdownMenuItem';

type LabelProps = React.ComponentPropsWithoutRef<'div'> & {
  inset?: boolean;
};

const ShadDropdownMenuLabel = React.forwardRef<HTMLDivElement, LabelProps>(
  ({ className, inset = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-xs3 py-xs2 text-body-small font-medium text-secondary',
        inset && 'pl-sm3',
        className,
      )}
      {...props}
    />
  ),
);
ShadDropdownMenuLabel.displayName = 'ShadDropdownMenuLabel';

const ShadDropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Menu.Separator>
>(({ className, ...props }, ref) => (
  <Menu.Separator
    ref={ref}
    className={cn('-mx-xxs1 my-xxs1 h-px bg-border-subtle', className)}
    {...props}
  />
));
ShadDropdownMenuSeparator.displayName = 'ShadDropdownMenuSeparator';

export {
  ShadDropdownMenu,
  ShadDropdownMenuContent,
  ShadDropdownMenuItem,
  ShadDropdownMenuLabel,
  ShadDropdownMenuSeparator,
  ShadDropdownMenuTrigger,
};

export const ShadDropdownMenuChevron = ({
  className,
}: {
  className?: string;
}) => (
  <ChevronDownIcon className={cn('size-[16px] text-secondary', className)} />
);
